/**
 * CriticalNumberGauge Component
 * Cash Runway gauge — the Scaling Up "Critical Number"
 * Target: ≥3 months
 */

import { StatusBadge } from './StatusBadge';
import { CalcTooltip } from './CalcTooltip';
import { CommentButton } from './CommentButton';
import { formatCurrency } from '@/utils/formatters';
import type { CashRunwayGauge as GaugeData } from '@/types/scalingUp';

interface CriticalNumberGaugeProps {
  data: GaugeData | null;
  loading: boolean;
  onInsight?: () => void;
}

function gaugeColor(status: string): string {
  if (status === 'healthy') return 'var(--success, #2C8248)';
  if (status === 'warning') return 'var(--warning, #d97706)';
  return 'var(--error, #dc2626)';
}

function runwayLabel(months: number): string {
  if (months >= 999) return 'No burn rate';
  if (months >= 12) return `${months.toFixed(0)} months`;
  return `${months.toFixed(1)} months`;
}

export function CriticalNumberGauge({ data, loading, onInsight }: CriticalNumberGaugeProps) {
  if (loading && !data) {
    return (
      <div className="scaling-up__card scaling-up__card--gauge">
        <h2 className="scaling-up__card-title">Critical Number</h2>
        <div className="skeleton" style={{ height: '120px' }} />
      </div>
    );
  }

  if (!data) return null;

  const fillPct = data.monthsRemaining >= 999
    ? 100
    : Math.min(100, (data.monthsRemaining / (data.target * 1.5)) * 100);

  return (
    <div className="scaling-up__card scaling-up__card--gauge">
      <div className="scaling-up__card-header">
        <h2 className="scaling-up__card-title">
          Critical Number
          <CalcTooltip title="Cash Runway Calculation">
            <p><strong>Formula:</strong> <code>Cash ÷ Monthly Burn</code></p>
            <p><strong>Cash at Bank</strong>: {formatCurrency(data.totalCash)} (Xero Balance Sheet)</p>
            <p><strong>Monthly Burn</strong>: {formatCurrency(data.monthlyBurnRate)} (FY expenses ÷ months)</p>
            <p><strong>Target</strong>: ≥{data.target} months.</p>
          </CalcTooltip>
        </h2>
        <StatusBadge status={data.status} label={data.status} size="sm" />
        {onInsight && (
          <button
            className="trend-chart__ai-btn"
            onClick={onInsight}
            title="AI Insights — cash runway analysis"
            aria-label="AI Insights for Critical Number"
          >
            AI
          </button>
        )}
        <CommentButton targetType="metric" targetId="cash-runway" />
      </div>
      <div className="gauge">
        <div className="gauge__label">Cash Runway</div>
        <div className="gauge__value" style={{ color: gaugeColor(data.status) }}>
          {runwayLabel(data.monthsRemaining)}
        </div>
        <div className="gauge__bar">
          <div
            className="gauge__fill"
            style={{
              width: `${fillPct}%`,
              backgroundColor: gaugeColor(data.status),
            }}
            role="progressbar"
            aria-valuenow={data.monthsRemaining}
            aria-valuemin={0}
            aria-valuemax={data.target * 1.5}
            aria-label={`Cash runway: ${runwayLabel(data.monthsRemaining)}`}
          />
          <div
            className="gauge__target"
            style={{ left: `${(data.target / (data.target * 1.5)) * 100}%` }}
            title={`Target: ${data.target} months`}
          />
        </div>
        <div className="gauge__details">
          <span>Cash: {formatCurrency(data.totalCash)}</span>
          <span>Burn: {formatCurrency(data.monthlyBurnRate)}/mo</span>
          <span>Target: {data.target}mo</span>
        </div>
      </div>
    </div>
  );
}
